import { DeleteDialog, GlobalMenu } from '@components';
import { useState } from '@hookstate/core';
import { useContext, useState as classicUseState } from 'react';
import { NavigationContext } from '@layout/navigation/navigation';
import { ICustomField } from '@models/CustomField.model';
import {
  deleteCustomField,
  getContentLength,
} from '@services/queries/customField';
import { useQueryClient, useMutation, useQuery } from 'react-query';
import { InviteUserRole } from '@models/Invite.model';
import { Container } from './CustomFieldsMenu.styles';
import { CustomFieldDetails } from '../CustomFieldDetails/CustomFieldDetails';

interface ICustomFieldsMenu {
  customField: ICustomField;
  customFields: ICustomField[];
}

const CustomFieldsMenu = ({ customField, customFields }: ICustomFieldsMenu) => {
  const queryClient = useQueryClient();
  const { studioId, role } = useContext(NavigationContext);
  const [editCustomField, setEditCustomField] = classicUseState(false);

  const openDeleteDialog = useState(false);

  const mutationDeleteCustomField = useMutation(deleteCustomField, {
    onSuccess: () => {
      queryClient.invalidateQueries(['studios', studioId, 'customField']);
    },
  });

  const { data: length, isLoading } = useQuery(
    ['customField', customField.id, 'contentLength'],
    () => getContentLength(customField.id),
  );

  return (
    <Container>
      <GlobalMenu
        align="end"
        title="Actions"
        itemList={[
          {
            disabled: ![InviteUserRole.ADMIN].includes(role),
            disableToolTip: [InviteUserRole.ADMIN].includes(role),
            toolTipText: 'Only admins can edit custom fields.',
            toolTipId: 'editFieldsTooltip',
            label: 'Edit',
            iconName: 'edit',
            onClick: () => {
              setEditCustomField(true);
            },
          },
          {
            disabled: ![InviteUserRole.ADMIN].includes(role),
            disableToolTip: [InviteUserRole.ADMIN].includes(role),
            toolTipText: 'Only admins can delete custom fields.',
            toolTipId: 'deleteFieldTooltip',
            label: 'Delete',
            iconName: 'trash',
            onClick: () => {
              openDeleteDialog.set(true);
            },
          },
        ]}
      />

      <DeleteDialog
        hideCancel
        width={379}
        open={openDeleteDialog}
        onDelete={() =>
          mutationDeleteCustomField.mutate({ studioId: customField.id })
        }
        text={
          length && length > 0
            ? `There are ${length} assets with this custom field. This action causes loss of data and cannot be undone.`
            : undefined
        }
      />

      {editCustomField && (
        <CustomFieldDetails
          open={editCustomField}
          onClose={() => {
            setEditCustomField(false);
          }}
          customField={customField}
          customFields={customFields}
          isLoading={isLoading}
          contentLength={length}
        />
      )}
    </Container>
  );
};

export { CustomFieldsMenu };
